import { z } from "zod";
import { articleUpsertSchema } from "./article";
import { mediaUploadSchema } from "./media";
import { faqUpsertSchema } from "./faq";
import { createFlowRequestSchema } from "./flows";
import { formTemplateContentSchema } from "./intake-template";
import { upsertJourneyPhaseSchema } from "./journey-phase";
import { createJourneyRequestSchema } from "./journeys";
import { dailyTipUpsertSchema } from "./daily-tip";
import { journeyStepCopyUpsertSchema } from "./journey-step-copy";
import { weeklyThemeUpsertSchema } from "./weekly-theme";
import { upsertWellnessFocusSchema } from "./wellness";
import { symptomGuidePostUpsertSchema } from "./symptom-guide";

// TES-297 [J17] — Bulk JSON/CSV content import.
//
// The stated purpose of the whole ops spec: let Codex (or any content author)
// load a batch of authorable content efficiently instead of one HTTP POST per
// entity. This is a THIN WRAPPER over the existing single-entity, doctor-guarded
// `employee-api/v1/*` create/upsert services — it does NOT re-implement any
// entity's validation or business logic. Each item's `data` is validated by the
// SAME contract schema the target service already uses, then dispatched to that
// service unchanged.
//
// A package is a flat list of typed entities. The envelope validates only the
// SHAPE (`type` is a known entity, `data` is an opaque object) — it deliberately
// does NOT strictly validate every item's `data` up front, because a bulk import
// is partial-success: one malformed row must fail on its own without rejecting
// the whole batch. Per-item `data` validation therefore happens inside the loop
// (see `bulkImportSchemaByType`), so a single failure is isolated to its item.

// The authorable entity kinds a bulk package may carry. Each maps 1:1 to an
// existing create/upsert service. `content_package` is a Flow with kind
// 'package' (TES-284) — same flow-authoring path as `flow`, distinguished only
// so the summary/report can attribute it.
export const bulkImportEntityTypeSchema = z.enum([
  "flow",
  "journey",
  "journey_phase",
  "form",
  "wellness_template",
  "weekly_theme",
  "journey_step_copy",
  "daily_tip",
  "article",
  "faq",
  // Media support — curated content images as base64.
  "media_asset",
  "content_package",
  // TES-1253 — a "Besvärsguiden" post, one per symptom code of the register.
  "symptom_guide_post",
]);
export type BulkImportEntityType = z.infer<typeof bulkImportEntityTypeSchema>;

// The per-type validation schema — the SAME schema the target service parses
// with, reused so we never fork an entity's validation rules. The service layer
// runs `bulkImportSchemaByType[item.type].safeParse(item.data)` per item; the
// target service re-parses too (belt + suspenders), which is cheap.
export const bulkImportSchemaByType: Record<
  BulkImportEntityType,
  z.ZodTypeAny
> = {
  flow: createFlowRequestSchema,
  content_package: createFlowRequestSchema,
  journey: createJourneyRequestSchema,
  journey_phase: upsertJourneyPhaseSchema,
  form: formTemplateContentSchema,
  wellness_template: upsertWellnessFocusSchema,
  weekly_theme: weeklyThemeUpsertSchema,
  journey_step_copy: journeyStepCopyUpsertSchema,
  daily_tip: dailyTipUpsertSchema,
  article: articleUpsertSchema,
  media_asset: mediaUploadSchema,
  faq: faqUpsertSchema,
  symptom_guide_post: symptomGuidePostUpsertSchema,
};

// A single entity in the package. Both `type` and `data` stay loose at the
// envelope level ON PURPOSE (see file header): a bulk import is partial-success,
// so an unknown `type` OR a malformed `data` must fail on its OWN item, not
// reject the whole batch at the envelope. `type` is therefore a plain string
// here and is validated against the known-entity set inside the loop; `data` is
// validated by the per-type schema in the loop.
export const bulkImportEntitySchema = z.object({
  type: z.string().min(1),
  data: z.unknown(),
});
export type BulkImportEntity = z.infer<typeof bulkImportEntitySchema>;

// Cap the batch so a single request can't be an unbounded write amplifier.
export const BULK_IMPORT_MAX_ENTITIES = 500;

// TES-349 — how a re-imported entity is handled:
//   create  — the default + legacy behaviour: a fresh create, so re-importing
//             an existing identity fails (409) / duplicates (faq).
//   replace — idempotent upsert by identity: update the existing row in place
//             (or create it if absent), so a content SYNC can safely re-run.
// Applies to the whole batch. Per entity, "identity" is: key (flow / journey /
// journey_phase / article), dimension (wellness_template), phaseKey
// (weekly_theme), key (faq — only when the payload carries one), or
// symptomCode (symptom_guide_post, TES-1253). `form` is already an upsert
// regardless.
export const bulkImportModeSchema = z.enum(["create", "replace"]);
export type BulkImportMode = z.infer<typeof bulkImportModeSchema>;

// The JSON "package": a flat, ordered list of typed entities + the conflict
// mode. TES-361 — `dryRun: true` runs the FULL per-item pipeline (zod schema,
// the silent-downgrade guards, the referential warning checks) and reports a
// per-item verdict (`valid | failed` + `warnings`) WITHOUT writing any entity.
// A batch-level audit row is still recorded (with `dryRun: true` metadata).
export const bulkImportRequestSchema = z.object({
  mode: bulkImportModeSchema.default("create"),
  dryRun: z.boolean().default(false),
  entities: z
    .array(bulkImportEntitySchema)
    .min(1)
    .max(BULK_IMPORT_MAX_ENTITIES),
});
export type BulkImportRequest = z.infer<typeof bulkImportRequestSchema>;

// Per-item outcome. `status` distinguishes a fresh create from an in-place
// upsert (`updated`, e.g. a form draft) from a failure; `valid` is the dry-run
// verdict (would have been written). `id` is the created/updated entity's
// stable identifier (id, key or dimension depending on the entity); `error` is
// a human-readable reason on failure (never PHI).
export const bulkImportItemStatusSchema = z.enum([
  "created",
  "updated",
  "failed",
  // dryRun only — the item passed every check and would have been written.
  "valid",
]);
export type BulkImportItemStatus = z.infer<typeof bulkImportItemStatusSchema>;

export const bulkImportItemResultSchema = z.object({
  index: z.number().int().min(0),
  // The raw `type` string as submitted — echoed back even when it is not a
  // known entity kind, so the caller can tell which item was rejected.
  type: z.string(),
  status: bulkImportItemStatusSchema,
  id: z.string().optional(),
  error: z.string().optional(),
  // TES-361 — machine-readable referential/visibility warnings. NON-fatal: the
  // item still imports; the warning flags content that won't reach a patient
  // as-is. Codes: `article_key_not_found:<key>` (flow item references an
  // article with no published row), `member_flow_not_found:<key>` /
  // `member_flow_not_published:<key>` (journey member missing/unpublished),
  // `coach_key_not_deliverable` (coach_message article whose key the coach
  // journey never sends), `inert_surfaces_only` (a non-chat flow item whose
  // surfaces contain none of RENDERED_SURFACES — renders nowhere).
  warnings: z.array(z.string()).optional(),
});
export type BulkImportItemResult = z.infer<typeof bulkImportItemResultSchema>;

export const bulkImportResultSchema = z.object({
  results: z.array(bulkImportItemResultSchema),
  summary: z.object({
    total: z.number().int().min(0),
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
    failed: z.number().int().min(0),
    // dryRun only — how many items passed validation (absent on real imports).
    valid: z.number().int().min(0).optional(),
  }),
});
export type BulkImportResult = z.infer<typeof bulkImportResultSchema>;
